import { useEffect, useState } from "react";
import * as api from "../api/client";
import { useCatalogo } from "../api/queries";
import {
  construirOrdenProduccion,
  datosDeOrden,
  datosVacios,
  ENTREGABLES,
  type DatosProduccion,
  type OrdenRegistrada,
} from "../domain/ordenProduccion";
import { cotizacionesPropuestas } from "../domain/propuestas";
import type { Cotizacion, Vendedor } from "../domain/types";
import { useEditor } from "../state/editor";
import { Campo, ErrorAviso, Modal } from "./ui";
import Icono from "./Icono";

// Botón al final de la cotización: abre el formulario con los datos que la
// cotización no trae (fechas, entregables, enlaces, observaciones) y descarga
// la hoja de orden a producción de la versión guardada. Con varias propuestas
// hay un botón por cada una (a planta va solo la que aprobó el cliente).
export default function OrdenProduccion({ vendedor }: { vendedor: Vendedor }) {
  const cotizacion = useEditor((s) => s.cotizacion),
    propuestas = useEditor((s) => s.propuestas),
    dirty = useEditor((s) => s.dirty),
    [abierta, setAbierta] = useState<Cotizacion | null>(null);
  const todas = cotizacionesPropuestas(cotizacion, propuestas);
  const sinGuardar = dirty || todas.some((c) => !c.numeroOficial);
  return (
    <section className="actions card">
      <h2>Orden a producción</h2>
      {sinGuardar ? (
        <p className="notice">
          Guarda la cotización para descargar la orden de producción. La hoja
          sale de la versión guardada.
        </p>
      ) : null}
      <div className="row">
        {todas.map((c) => (
          <button
            key={c.numero}
            disabled={sinGuardar}
            onClick={() => setAbierta(c)}
          >
            <Icono nombre="descargar" />
            {`Descargar orden de producción${todas.length > 1 ? ` · ${c.etiquetaPropuesta}` : ""}`}
          </button>
        ))}
      </div>
      {abierta ? (
        <FormularioOrden
          cotizacion={abierta}
          vendedor={vendedor}
          onClose={() => setAbierta(null)}
        />
      ) : null}
    </section>
  );
}

function FormularioOrden({
  cotizacion: c,
  vendedor,
  onClose,
}: {
  cotizacion: Cotizacion;
  vendedor: Vendedor;
  onClose: () => void;
}) {
  const ciudades = useCatalogo().data?.ciudades,
    [datos, setDatos] = useState<DatosProduccion>(datosVacios),
    [registrada, setRegistrada] = useState<OrdenRegistrada | null>(null),
    [cargando, setCargando] = useState(true),
    [generando, setGenerando] = useState(false),
    [error, setError] = useState<unknown>();
  // Prellena con lo ya guardado: volver a descargar no obliga a reescribir.
  useEffect(() => {
    const control = new AbortController();
    api
      .ordenProduccionRegistrada(vendedor.id, c.registroId!, control.signal)
      .then((o) => {
        setRegistrada(o);
        setDatos(datosDeOrden(o));
      })
      .catch((e) => {
        if (!control.signal.aborted) setError(e);
      })
      .finally(() => {
        if (!control.signal.aborted) setCargando(false);
      });
    return () => control.abort();
  }, [vendedor.id, c.registroId]);
  // En planta la orden ya no cambia: el backend conserva lo guardado.
  const congelada = registrada != null && registrada.estado !== "pendiente";
  const set = <K extends keyof DatosProduccion>(k: K, v: DatosProduccion[K]) =>
    setDatos((d) => ({ ...d, [k]: v }));
  const fecha = (k: keyof DatosProduccion, label: string) => (
    <Campo label={label}>
      <input
        type="date"
        value={datos[k] as string}
        onChange={(e) => set(k, e.target.value)}
      />
    </Campo>
  );
  const enlace = (k: keyof DatosProduccion, label: string) => (
    <Campo label={label}>
      <input
        type="url"
        inputMode="url"
        placeholder="https://…"
        value={datos[k] as string}
        onChange={(e) => set(k, e.target.value)}
      />
    </Campo>
  );
  async function generar() {
    setGenerando(true);
    setError(null);
    try {
      const blob = await api.ordenProduccionPdf(
        construirOrdenProduccion(c, vendedor, ciudades, datos),
      );
      const url = URL.createObjectURL(blob),
        a = document.createElement("a");
      a.href = url;
      a.download = `Orden-produccion-${c.numeroOficial}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setGenerando(false);
    }
  }
  return (
    <Modal
      title={`Orden de producción · ${c.numeroOficial}${c.etiquetaPropuesta ? ` · ${c.etiquetaPropuesta}` : ""}`}
      onClose={onClose}
    >
      {cargando ? (
        <p role="status">Cargando datos de la orden…</p>
      ) : (
        <form
          className="orden-form"
          onSubmit={(e) => {
            e.preventDefault();
            void generar();
          }}
        >
          {registrada ? (
            <p className="notice">
              {congelada
                ? `La orden ${registrada.numero_orden} ya está en producción: se descarga tal como quedó registrada.`
                : `Orden ${registrada.numero_orden} registrada. Puedes corregir estos datos mientras siga pendiente.`}
            </p>
          ) : (
            <p className="notice">
              Todos los campos son opcionales; lo que quede vacío sale como "Por
              definir" en la hoja.
            </p>
          )}
          <fieldset disabled={congelada || generando}>
            <legend>Cronograma</legend>
            <div className="grid three">
              {fecha(
                "fecha_entrega_comprometida",
                "Fecha de entrega comprometida",
              )}
              {fecha(
                "fecha_presentacion_diseno",
                "Fecha de presentación de diseño",
              )}
              {fecha("fecha_implementacion", "Fecha de implementación")}
            </div>
          </fieldset>
          <fieldset disabled={congelada || generando}>
            <legend>Entregables</legend>
            <div className="row">
              {ENTREGABLES.map(([k, texto]) => (
                <label className="check" key={k}>
                  <input
                    type="checkbox"
                    checked={datos.entregables.includes(k)}
                    onChange={(e) =>
                      set(
                        "entregables",
                        e.target.checked
                          ? [...datos.entregables, k]
                          : datos.entregables.filter((x) => x !== k),
                      )
                    }
                  />
                  {texto}
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset disabled={congelada || generando}>
            <legend>Adjuntos (enlaces)</legend>
            <div className="grid two">
              {enlace("arte_final_url", "Arte final aprobado")}
              {enlace("plano_tecnico_url", "Plano técnico")}
              {enlace("despiece_url", "Despiece / BOM detallado")}
              {enlace(
                "levantamiento_url",
                "Fotos de referencia / levantamiento",
              )}
            </div>
          </fieldset>
          <fieldset disabled={congelada || generando}>
            <legend>Observaciones para producción</legend>
            <textarea
              aria-label="Observaciones para producción"
              maxLength={4000}
              value={datos.observaciones}
              onChange={(e) => set("observaciones", e.target.value)}
            />
          </fieldset>
          <ErrorAviso error={error} />
          <footer className="row end dialogo-acciones">
            <button type="button" onClick={onClose} disabled={generando}>
              Cancelar
            </button>
            <button className="primary" disabled={generando}>
              <Icono nombre={generando ? "cargar" : "descargar"} />
              {generando ? "Generando orden…" : "Generar y descargar"}
            </button>
          </footer>
        </form>
      )}
    </Modal>
  );
}
